import { defineStore } from "pinia";
import moment, { Moment } from 'moment';

import { DashboardIndicators } from  "~/api/models/dashboard/dashboard-indicators.model";
import { DashboardSlots } from  "~/api/models/dashboard/dashboard-history.model";


import { DashboardFilterEnum } from "~/models/dashboard/dashboard-filter.enum";
import { StationGroup } from "~/api/models/station-group.model";
import { useStationStore } from "./station.store";
import { DashboardStoreError, DashboardStoreSocketError } from "~/exceptions/store.error";
import io,  { Manager } from 'socket.io-client';
import { useAuthStore } from "./auth.store";
import { useConfigStore } from "./configuration.store";
import { Measure } from "~/api/models/measure.model";
import { Station } from "~/api/models/station.model";
import { getAlarmFromValues } from "~/misc/values-utils";
import { AvoltaAlarmEnum } from "~/configs/alarms/avolta-alarm.model";

type StationMeasures = { [id: string]: Measure[]};

export const useDashboardStore = defineStore('dashboard', {
    state: () => {
        const since: Moment = moment().subtract(1, 'month').startOf('day');
        const before: Moment = moment().endOf('days');
        return {
            indicators: null as DashboardIndicators,
            history: [] as (DashboardSlots[]),
            measures: {} as StationMeasures,
            loading: false,
    
            since,
            before,
            step: before.diff(since, 'seconds') / 60,
    
            filter: DashboardFilterEnum.ALL as DashboardFilterEnum,

            socket: null as Manager,
        }
    },

    actions: {

        initSocket(): void {

            if(this.socket) {
                this.socket.close();
                this.socket = null;
            }

            try {
                const authStore = useAuthStore();
                const group: StationGroup = useStationStore().group;
    
                const socketEndpoint: string = useConfigStore().socketEndpoint;
                
                this.socket = io(socketEndpoint, {
                    query: {
                        topic: `stations.${group?.name}`,
                        token: authStore.accessToken,
                    },
                    withCredentials: true,
                });
    
                this.socket.on("measures", () => this.refresh(false));
            } catch(error) {
                throw new DashboardStoreSocketError('Socket could not be initialized', error);
            }


        },

        async refresh(full: boolean = true): Promise<void> {

            const group: StationGroup = useStationStore().group;
            if(!group) {
                throw new DashboardStoreError('No group selected');
            }

            const stations: Station[] = useStationStore().stations || [];

            if (this.loading) {
                return;
            }

            this.loading = true;
            try {
                if(full) {
                    this.history = await this .$dashboardService.getHistory(group.name, this.since, this.before, this.step);
                }
                
                useStationStore().refresh();
                this.indicators = await this.$dashboardService.getIndicators(group.name, this.since, this.before);
                this.measures = await Promise.all(
                    stations.map(s => this.$stationService.getMeasures(s.id, moment().startOf('day')))
                ).then((measures: Measure[][]) => {
                    return measures.reduce((prev: StationMeasures, measures: Measure[], index: number) => {
                        const stationId: string = stations[index].id;

                        if(!stationId) {
                            return prev;
                        }

                        prev[stationId] = measures;
                        return prev;

                    }, {});
                });
                this.computeLastHistorySlot(stations);

            } catch(error) {
                this.indicators = null;
                this.history = null;
                this.loading = false;
                throw new DashboardStoreError('Could not refresh dashboard', error)
            }

            this.loading = false;
        },

        async setTimerange(since?: Moment, before?: Moment) {
            
            since = since ? since : this.since;
            before = before ? before : this.before;

            if(!since || !before) {
                return;
            }

            this.since = since;
            this.before = before;

            const duration = this.before.diff(this.since, 'months');
            
            if(duration < 3){
                this.step = 24 * 3600;
            } else  {
                this.step = 2 * 24 * 3600;
            }
        },

        computeLastHistorySlot(stations: Station[]): void {

            const lastSlot: DashboardSlots = this.history.length ? this.history[this.history.length - 1]: null;

            let slot: DashboardSlots = null;
            if(lastSlot && moment.unix(lastSlot.end).isAfter(moment().startOf('day'))) {
                slot = this.history.pop();
                slot = {
                    ...slot,
                    gas: [],
                    trouble: [],
                    ok: [],
                    online: [],
                    offline: [],
                }
            } else {
                const start: Moment = lastSlot ? moment.unix(lastSlot.end).add(1, 'seconds'): moment().startOf('days');
                const end: Moment = start.clone().add(1, 'days').subtract(1, 'seconds');
                slot = {
                    start: start.unix(),
                    end: end.unix(),
                    gas: [],
                    trouble: [],
                    ok: [],
                    online: [],
                    offline: [],
                };
            }
        
            stations.forEach(station => {
                const measures = this.measures[station?.id];
                
                if(!measures) {
                    slot.offline.push(station?.id);
                } else if(station.status === 'offline') {
                    slot.offline.push(station?.id);
                } else {
                    slot.online.push(station?.id);
                }
                
                const gas: Set<string> = new Set();
                const trouble: Set<string> = new Set();
                const ok: Set<string> = new Set();
                
                let deviceAlarms = getAlarmFromValues(station.latest_values);
                let hasIssue = false;
                if(deviceAlarms.includes(AvoltaAlarmEnum.CH4_concentration_alarm)) {
                    hasIssue = true;
                    gas.add(station?.id);
                }
                
                // filter out gas and offline alarms
                deviceAlarms = deviceAlarms.filter(a => ![AvoltaAlarmEnum.CH4_concentration_alarm, AvoltaAlarmEnum.offline].includes(a));
                if(deviceAlarms.length > 0) {
                    hasIssue = true;
                    trouble.add(station?.id);
                }
                
                if(!hasIssue) {
                    ok.add(station?.id);
                }

                measures.forEach((m: Measure)=> {
                    const alarms = getAlarmFromValues(m.values);
                    let hasIssue: boolean = false;

                    if(alarms.includes(AvoltaAlarmEnum.CH4_concentration_alarm)) {
                        gas.add(station?.id);
                        ok.delete(station?.id);
                        hasIssue = true;
                    }

                    if(alarms.filter(a => a !== AvoltaAlarmEnum.CH4_concentration_alarm).length) {
                        trouble.add(station?.id);
                        ok.delete(station?.id);
                        hasIssue = true;
                    }

                    if(!hasIssue) {
                        ok.add(station?.id);
                    }
                    
                });

                slot.gas = slot.gas.concat(Array.from(gas));
                slot.trouble = slot.trouble.concat(Array.from(trouble));
                slot.ok = slot.ok.concat(Array.from(ok));

            });
            
            this.history.push(slot);
        }

    }
});